<script setup>
  const lastUpdated = "April 23, 2025"
  import { useHead } from "@vueuse/head"
  import BasePage from "./BasePage.vue"

  const socialImage = "https://untrackedapps.com/untracked_apps_logo.png"

  useHead({
    title: "Privacy Policy – Untracked Apps",
    meta: [
      // SEO
      {
        name: "description",
        content:
          "Our privacy policy is simple: we don’t track you. Learn more about how Untracked Apps protects your data.",
      },
      {
        name: "keywords",
        content:
          "privacy policy, data protection, no tracking, Untracked Apps, privacy-first",
      },
      {
        name: "author",
        content: "Untracked Apps",
      },

      // Open Graph
      {
        property: "og:type",
        content: "website",
      },
      {
        property: "og:title",
        content: "Privacy Policy – Untracked Apps",
      },
      {
        property: "og:description",
        content:
          "Untracked Apps respects your privacy with transparent, privacy-first data practices.",
      },
      {
        property: "og:image",
        content: socialImage,
      },
      {
        property: "og:url",
        content: "https://untrackedapps.com/privacy",
      },
      {
        property: "og:site_name",
        content: "Untracked Apps",
      },

      // Twitter Cards
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:title",
        content: "Privacy Policy – Untracked Apps",
      },
      {
        name: "twitter:description",
        content:
          "We don’t track you. No ad IDs. No analytics. Learn more about how we protect your data.",
      },
      {
        name: "twitter:image",
        content: socialImage,
      },
      {
        name: "twitter:site",
        content: "@UntrackedApps",
      },
    ],
    link: [
      {
        rel: "canonical",
        href: "https://untrackedapps.com/privacy",
      },
    ],
  })
</script>

<template>
  <BasePage>
    <section
      class="min-h-[100vh] px-6 py-10 relative overflow-hidden max-w-3xl mx-auto"
    >
      <header class="text-center">
        <h1 class="text-4xl md:text-5xl font-bold text-gray-700">
          Privacy Policy
        </h1>
        <p class="mt-4 mb-8 text-lg">Last updated: {{ lastUpdated }}</p>
      </header>

      <section class="space-y-6 text-lg leading-relaxed text-left">
        <p>
          At Untracked Apps, your privacy isn’t just a feature — it’s a
          principle. We believe you should be able to use great apps without
          being monitored, tracked, or profiled.
        </p>

        <p>
          This policy applies to all websites, apps, and services developed or published by Untracked Apps, including any apps listed under our developer account on platforms like the Apple App Store.
        </p>
        <p>
          Untracked Apps is a product brand operated by Dominic Juan Lebron and is not a separate legal entity.
        </p>

        <h2 class="text-xl font-semibold mt-8 mb-2">What We Don’t Do</h2>
        <ul class="space-y-4 list-disc list-outside pl-6 text-left">
          <li>
            <strong class="text-gray-700">No tracking.</strong> We don’t use
            Google Analytics, Facebook pixels, or any third-party tracking
            scripts.
          </li>
          <li>
            <strong class="text-gray-700">No hidden ad tracking.</strong> If an app includes ads, tracking and data use are disclosed clearly in that app’s listing and policy.
          </li>
          <li>
            <strong class="text-gray-700">No cookies.</strong> Our apps don’t
            set or store cookies, unless absolutely required (e.g., session
            authentication).
          </li>
          <li>
            <strong class="text-gray-700">No data harvesting.</strong> We don’t
            collect personal data unless you intentionally provide it — and
            we’ll always tell you why.
          </li>
          <li>
            <strong class="text-gray-700">Local-first design.</strong> Wherever
            possible, your data stays on your device. If cloud sync is offered,
            it will be opt-in and end-to-end encrypted.
          </li>
        </ul>

        <h2 class="text-xl font-semibold mt-8 mb-2">What We May Collect</h2>
        <p>
          Some apps may offer iCloud or platform-native sync options (e.g. iCloud Key-Value or CloudKit). These are provided via Apple’s infrastructure and are subject to Apple’s privacy and security policies.
        </p>

        <h2 class="text-xl font-semibold mt-8 mb-2">Need Help?</h2>
        <p>
          If you ever have questions about your privacy or how your data is handled, just reach out. We’re here to help — and we actually read our email. You can contact us at
          <a href="mailto:support@untrackedapps.com" class="text-blue-600 hover:underline">support@untrackedapps.com</a>.
        </p>
      </section>
    </section>
  </BasePage>
</template>
